import { useEffect } from "react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/motion";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const SECTIONS = [
  {
    title: "1. Acceptance of these terms",
    body: [
      "These Terms of Service (“Terms”) govern your use of the website newbeginningsrecovery.ca (the “Site”), operated by New Beginnings Recovery Home (“New Beginnings”, “we”, “us”, “our”), located at 69 Gradwell Drive, Scarborough, Ontario. By accessing or using the Site, you agree to be bound by these Terms. If you do not agree, please do not use the Site.",
    ],
  },
  {
    title: "2. Not medical advice",
    body: [
      "The content on this Site is provided for general informational purposes only. It is not medical advice, diagnosis or treatment, and it does not create a physician–patient, counsellor–client or any other professional relationship between you and New Beginnings.",
      "Always seek the advice of a qualified health provider with any questions about a medical or mental-health condition. Never disregard professional advice or delay seeking it because of something you read on this Site.",
    ],
  },
  {
    title: "3. Emergencies",
    body: [
      "This Site is not monitored for emergencies. If you or someone you know is in crisis or immediate danger, call 911 or go to the nearest emergency department. Talk Suicide Canada is available 24/7 at 1-833-456-4566.",
    ],
  },
  {
    title: "4. Eligibility and use of the Site",
    body: [
      "You may use this Site only for lawful purposes and in accordance with these Terms. You agree not to misuse the Site, including by attempting to gain unauthorized access to our systems, submitting false or misleading information, interfering with the Site’s operation, or scraping or reproducing Site content without permission.",
    ],
  },
  {
    title: "5. Enquiry and admissions forms",
    body: [
      "Submitting a contact, insurance-verification or admissions enquiry form does not guarantee admission, a response within any particular timeframe, or the availability of a bed. Insurance benefit information obtained through our verification process is an estimate only; final coverage decisions rest with your insurer.",
    ],
  },
  {
    title: "6. Testimonials and stories",
    body: [
      "Testimonials and recovery stories on this Site reflect the real experiences of individuals who chose to share them. They are shared with consent, may be edited for length or clarity, and do not guarantee any particular outcome. Recovery is individual, and results vary from person to person.",
    ],
  },
  {
    title: "7. Intellectual property",
    body: [
      "All content on this Site — including text, design, graphics, logos, photographs and video — is the property of New Beginnings Recovery Home or its licensors and is protected by Canadian and international copyright and trademark laws. You may not reproduce, distribute or create derivative works without our prior written consent. Third-party marks (including CARF, LegitScript, Psychology Today and insurer logos) remain the property of their respective owners and are displayed for identification purposes only.",
    ],
  },
  {
    title: "8. Third-party links and services",
    body: [
      "The Site contains links to third-party websites and services, including donation platforms (Zeffy), payment processors (Stripe), video hosting (Google Drive) and community resources. We do not control and are not responsible for the content, accuracy or privacy practices of third-party sites. Their own terms and privacy policies apply.",
    ],
  },
  {
    title: "9. Donations and payments",
    body: [
      "Donations made through third-party platforms are subject to those platforms’ terms. Program fees, payment schedules, refund and cancellation terms for treatment services are set out in your individual admission agreement, which governs in the event of any conflict with these Terms.",
    ],
  },
  {
    title: "10. Disclaimer of warranties",
    body: [
      "The Site is provided “as is” and “as available”, without warranties of any kind, express or implied, including warranties of accuracy, completeness, merchantability, fitness for a particular purpose or non-infringement. We do not warrant that the Site will be uninterrupted, error-free or free of viruses or other harmful components.",
    ],
  },
  {
    title: "11. Limitation of liability",
    body: [
      "To the fullest extent permitted by law, New Beginnings Recovery Home, its directors, staff and affiliates will not be liable for any indirect, incidental, consequential or punitive damages, or any loss of data, profits or goodwill, arising from your use of or inability to use the Site or reliance on its content.",
    ],
  },
  {
    title: "12. Indemnification",
    body: [
      "You agree to indemnify and hold harmless New Beginnings Recovery Home from any claims, damages or expenses arising from your violation of these Terms or misuse of the Site.",
    ],
  },
  {
    title: "13. Governing law",
    body: [
      "These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable therein. Any dispute arising from these Terms or your use of the Site will be subject to the exclusive jurisdiction of the courts of Ontario.",
    ],
  },
  {
    title: "14. Changes and contact",
    body: [
      "We may revise these Terms from time to time; the “last updated” date below reflects the most recent revision. Continued use of the Site after changes are posted constitutes acceptance of the revised Terms.",
      `Questions about these Terms may be directed to: New Beginnings Recovery Home, 69 Gradwell Drive, Scarborough, ON M1M 2N1 · ${CONTACT.email} · ${CONTACT.phone}`,
    ],
  },
];

const TermsPage = () => {
  useSEO({
    title: "Terms of Service | New Beginnings Recovery Toronto",
    description: "Terms of Service for the New Beginnings Recovery Home website — Scarborough, Toronto addiction and mental-health inpatient care.",
    keywords: "terms of service, terms of use, New Beginnings Recovery",
    canonical: "https://newbeginningsrecovery.ca/terms",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="terms-page" className="bg-sand min-h-screen">
      <Navbar />
      <section className="pt-40 md:pt-48 pb-12">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">Legal</p>
            <h1 className="font-serif text-5xl md:text-6xl text-forest tracking-tight leading-[0.98]">
              Terms of Service
            </h1>
            <p className="mt-6 text-lg text-ink2 leading-relaxed">
              The terms that govern your use of this website. For the terms of your care itself,
              your admission agreement always governs.
            </p>
            <p className="mt-4 text-sm text-ink2/70">Last updated: July 2026</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[900px] px-5 md:px-10 space-y-10">
          {SECTIONS.map((s) => (
            <Reveal key={s.title}>
              <div>
                <h2 className="font-serif text-2xl md:text-3xl text-forest tracking-tight">{s.title}</h2>
                {s.body.map((p, i) => (
                  <p key={i} className="mt-3 text-ink2 leading-relaxed">{p}</p>
                ))}
              </div>
            </Reveal>
          ))}
          <Reveal>
            <p className="text-sm text-ink2/70 border-t border-line pt-6">
              These terms are provided for general informational purposes and do not constitute legal advice.
            </p>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TermsPage;
